import React, { useState, useEffect } from 'react';
import api from './api';

const RoughPurchaseHistory = () => {
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        loadHistory();
    }, []);

    const loadHistory = async () => {
        try {
            setLoading(true);
            const res = await api.get('/rough'); 
            
            const rawData = res.data.data || []; 

            // 🎯 UPGRADED FILTER: Keep ONLY original master purchases
            const originalPurchases = rawData.filter(p => {
                // 1. If it has a parentId, it's a split/child (Hide it)
                if (p.parentId) return false;
                
                // 2. If the Lot Number contains "-BAL", it's a balance lot (Hide it)
                // (You can also add || p.rpNumber.includes('-A') if you use letters for splits)
                if (p.rpNumber && p.rpNumber.includes('-BAL')) return false;

                // Otherwise, it is a true original purchase (Show it)
                return true;
            });

            // Sort the data (newest first)
            const sortedData = originalPurchases.sort((a, b) => {
                const dateA = new Date(a.purchaseDate || a.createdAt || 0);
                const dateB = new Date(b.purchaseDate || b.createdAt || 0);
                return dateB - dateA;
            });

            setPurchases(sortedData);
            
        } catch (err) {
            console.error("Failed to load rough history:", err);
            alert("Error loading data.");
        } finally {
            setLoading(false);
        }
    };

    // Bulletproof Filter
    const filteredPurchases = purchases.filter(p => {
        if (!searchTerm) return true; 
        
        const searchLower = searchTerm.toLowerCase();
        
        // Use the exact database fields
        const lotMatch = (p.rpNumber || "").toLowerCase().includes(searchLower);
            
        // Party is an object, so we safely check if it exists first
        const partyName = p.party ? (p.party.companyName || p.party.name || "") : "";
        const partyMatch = partyName.toLowerCase().includes(searchLower);
            
        return lotMatch || partyMatch;
    });

    // Calculate Grand Total for the currently filtered view (USING ORIGINAL WEIGHT)
    const totalCarats = filteredPurchases.reduce((sum, p) => sum + (Number(p.originalWeight || p.weight) || 0), 0);
    const totalValue = filteredPurchases.reduce((sum, p) => sum + ((Number(p.originalWeight || p.weight) || 0) * (Number(p.rate) || 0)), 0);

    // --- Custom Date Formatter (DD-MM-YYYY) ---
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return 'N/A';
        
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        
        return `${day}-${month}-${year}`; 
    };
    // ------------------------------------------

    return (
        <div className="container mt-4 mb-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="fw-bold text-primary">📦 Rough Purchase History</h3>
                <input 
                    type="text" 
                    className="form-control border-primary shadow-sm" 
                    style={{ maxWidth: '300px' }}
                    placeholder="🔍 Search Lot or Party..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="card shadow-sm border-0">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="table-dark">
                            <tr>
                                <th>Date</th>
                                <th>Lot No.</th>
                                <th>Supplier / Party</th>
                                <th className="text-end">Weight (Cts)</th>
                                <th className="text-end">Rate ($/₹)</th>
                                <th className="text-end">Total Amount</th>
                                <th className="text-center">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" className="text-center py-4">Loading data...</td></tr>
                            ) : filteredPurchases.length === 0 ? (
                                <tr><td colSpan="7" className="text-center py-4 text-muted">No records found.</td></tr>
                            ) : (
                                filteredPurchases.map(p => (
                                    <tr key={p._id}>
                                        {/* Use purchaseDate instead of createdAt */}
                                        <td className="text-muted fw-medium">{formatDate(p.purchaseDate || p.createdAt)}</td>
                                        
                                        {/* Use rpNumber */}
                                        <td className="fw-bold text-primary">{p.rpNumber}</td>
                                        
                                        {/* Safely display the Party Company Name */}
                                        <td>{p.party ? (p.party.companyName || p.party.name) : 'N/A'}</td>
                                        
                                        <td className="text-end fw-bold">{Number(p.originalWeight || p.weight).toFixed(2)}</td>
                                        <td className="text-end">{Number(p.rate).toLocaleString()}</td>
                                        <td className="text-end text-success fw-bold">
                                            {Number((p.originalWeight || p.weight || 0) * (p.rate || 0)).toLocaleString()}
                                        </td>
                                        <td className="text-center">
                                            <span className={`badge ${p.status === 'Active' ? 'bg-success' : 'bg-secondary'}`}>
                                                {p.status || 'Stock'}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        {/* Summary Footer */}
                        {!loading && filteredPurchases.length > 0 && (
                            <tfoot className="table-light fw-bold">
                                <tr>
                                    <td colSpan="3" className="text-end">TOTALS:</td>
                                    <td className="text-end">{totalCarats.toFixed(2)} Cts</td>
                                    <td></td>
                                    <td className="text-end text-success border-top border-2 border-dark">
                                        {totalValue.toLocaleString()}
                                    </td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
            
            <small className="text-muted mt-2 d-block">
                * Note: Total Amount is calculated as Weight × Rate. Showing {filteredPurchases.length} records.
            </small>
        </div>
    );
};

export default RoughPurchaseHistory;